import { MEASUREMENT_TECHNIQUE, TARGET_RESPONSE } from './guideline';

function a(num) {
  return Number(num) > 0 ? `+${num}` : num;
}

export function getTargetResponses(targetStats, criteria) {
  return targetStats.map((stat) => getTargetResponse(stat, criteria));
}

/**
 * Evaluate target response
 * @param {Object} stat - Statistical data containing lesion measurements.
 * @param {Object} criteria - Evaluation criteria.
 * @param {String} criteria.measurementTechnique - "BIDIMENSIONAL" or "VOLUMETRIC".
 * @param {Number} criteria.minDiameterIncrease - Minimum percent increase (as decimal, e.g. 0.2 for 20%).
 * @param {Number} criteria.minDiameterDecrease - Minimum percent decrease (as decimal, e.g. 0.3 for 30%).
 * @param {Number} criteria.minVolumeIncrease - For volumetric mode.
 * @param {Number} criteria.minVolumeDecrease - For volumetric mode.
 * @param {Number} [criteria.absoluteDiameterChange] - Optional absolute change threshold (mm).
 * @param {Number} [criteria.absoluteSumOfDiameterChange] - Optional sum of diameter change threshold (mm).
 * @returns {{ fromBaseline: number, fromNadir: number, result: string, message: string }}
 */
export function getTargetResponse(stat, criteria) {
  const measurementTechnique =
    criteria?.measurementTechnique || MEASUREMENT_TECHNIQUE.BIDIMENSIONAL;

  const keyMap = {
    [MEASUREMENT_TECHNIQUE.BIDIMENSIONAL]: {
      sum: 'sumDiameter',
      change: 'sumDiameter',
      increase: 'minDiameterIncrease',
      decrease: 'minDiameterDecrease',
    },
    [MEASUREMENT_TECHNIQUE.VOLUMETRIC]: {
      sum: 'sumVolume',
      change: 'sumVolume',
      increase: 'minVolumeIncrease',
      decrease: 'minVolumeDecrease',
    },
  };

  const key = keyMap[measurementTechnique];
  const minIncrease = criteria[key.increase] * 100;
  const minDecrease = criteria[key.decrease] * 100;

  const changeFromBaseline = stat.fromBaseline[key.change];
  const changeFromNadir = stat.fromNadir[key.change];
  const existAbsoluteDiameterChange =
    stat.fromNadir.existAbsoluteDiameterChange;
  const existAbsoulteSODChange =
    stat.fromNadir.existAbsoluteSumOfDiameterChange;

  let result;
  let message;

  function buildMessage(label, fromStat, optional = false) {
    const change = fromStat[key.change];
    const absolute = fromStat.absoluteSumOfDiameterChange;
    const fromTo = `from ${fromStat.baseline_timepoint_id} to ${fromStat.timepoint_id}`;
    const reason = `${a(change)}% change (${a(absolute)} mm) ${fromTo}`;

    // optional extra note (criteria / no change detail)
    let extra = '';
    if (optional && changeFromNadir >= minIncrease) {
      const change = stat.fromNadir[key.change];
      const absolute = stat.fromNadir.absoluteSumOfDiameterChange;
      const fromTo = `from ${stat.fromNadir.baseline_timepoint_id} to ${stat.fromNadir.timepoint_id}`;
      const { absoluteDiameterChange, absoluteSumOfDiameterChange } = criteria;
      if (absoluteSumOfDiameterChange && !existAbsoulteSODChange) {
        extra = `; ${a(change)}% change (${a(absolute)} mm) ${fromTo}`;
      } else if (absoluteDiameterChange && !existAbsoluteDiameterChange) {
        extra = `; no change in target lesion greater than ${absoluteDiameterChange} mm ${fromTo}`;
      }
    }

    return `Target Lesion(${label}): ${reason}${extra}`;
  }

  // Main decision logic
  if (
    changeFromNadir >= minIncrease &&
    existAbsoluteDiameterChange &&
    existAbsoulteSODChange
  ) {
    result = TARGET_RESPONSE.PROGRESSIVE_DISEASE;
    message = buildMessage('PD', stat.fromNadir);
  } else if (changeFromBaseline === -100) {
    result = TARGET_RESPONSE.COMPLETE_RESPONSE;
    message = buildMessage('CR', stat.fromBaseline);
  } else if (changeFromBaseline <= -minDecrease) {
    result = TARGET_RESPONSE.PARTIAL_RESPONSE;
    message = buildMessage('PR', stat.fromBaseline, true);
  } else {
    result = TARGET_RESPONSE.STABLE_DISEASE;
    message = buildMessage('SD', stat.fromBaseline, true);
  }

  // Secondary override logic
  const sum = stat.summation[key.sum];
  const { timepoint_id, baseline_timepoint_id } = stat.fromBaseline;

  if (timepoint_id !== baseline_timepoint_id && sum === 0) {
    /** Complete Response (override) */
    result = TARGET_RESPONSE.COMPLETE_RESPONSE;
    message = `Target Lesion(CR): no lesion found at ${timepoint_id}`;
  }

  if (stat.existLesions && !stat.existROIs) {
    /** Complete Response (no ROI found) */
    result = TARGET_RESPONSE.COMPLETE_RESPONSE;
    message = `Target Lesion(CR): no lesion found at ${timepoint_id}`;
  }

  if (!stat.existLesions) {
    /** No Evidence of Disease */
    result = TARGET_RESPONSE.NONE;
    message = `Target Lesion(NED): no target lesion selected`;
  }

  return {
    fromBaseline: changeFromBaseline,
    fromNadir: changeFromNadir,
    result,
    message,
  };
}
